"use client"

export type FOAChannelOrder = "ambix" | "fuma"

export interface FOADecoder {
  input: GainNode
  output: GainNode
  setChannelOrder: (order: FOAChannelOrder) => void
}

interface SpeakerDirection {
  azimuth: number
  elevation: number
}

const SPEAKERS: SpeakerDirection[] = [
  { azimuth: 45, elevation: 35.264 },
  { azimuth: -45, elevation: 35.264 },
  { azimuth: 135, elevation: 35.264 },
  { azimuth: -135, elevation: 35.264 },
  { azimuth: 45, elevation: -35.264 },
  { azimuth: -45, elevation: -35.264 },
  { azimuth: 135, elevation: -35.264 },
  { azimuth: -135, elevation: -35.264 },
]

function speakerComponents(direction: SpeakerDirection) {
  const azimuth = direction.azimuth * Math.PI / 180
  const elevation = direction.elevation * Math.PI / 180
  const horizontal = Math.cos(elevation)
  return {
    // Ambisonic axes: X=front, Y=left, Z=up.
    ambisonic: [1, Math.cos(azimuth) * horizontal, -Math.sin(azimuth) * horizontal, Math.sin(elevation)] as const,
    // Web Audio axes: +X=right, +Y=up, -Z=front.
    webAudio: [Math.sin(azimuth) * horizontal, Math.sin(elevation), -Math.cos(azimuth) * horizontal] as const,
  }
}

export function createFOADecoder(context: AudioContext): FOADecoder {
  const input = context.createGain()
  input.channelCount = 4
  input.channelCountMode = "explicit"
  input.channelInterpretation = "discrete"

  const splitter = context.createChannelSplitter(4)
  const output = context.createGain()
  const matrix = SPEAKERS.map(() => Array.from({ length: 4 }, () => context.createGain()))

  input.connect(splitter)

  SPEAKERS.forEach((direction, speakerIndex) => {
    const components = speakerComponents(direction)
    const sum = context.createGain()
    const panner = context.createPanner()
    panner.panningModel = "HRTF"
    panner.distanceModel = "linear"
    panner.refDistance = 1
    panner.maxDistance = 1
    panner.rolloffFactor = 0
    panner.coneInnerAngle = 360
    panner.coneOuterAngle = 360
    panner.positionX.value = components.webAudio[0]
    panner.positionY.value = components.webAudio[1]
    panner.positionZ.value = components.webAudio[2]

    for (let channel = 0; channel < 4; channel += 1) {
      splitter.connect(matrix[speakerIndex][channel], channel)
      matrix[speakerIndex][channel].connect(sum)
    }

    sum.connect(panner).connect(output)
  })

  const setChannelOrder = (order: FOAChannelOrder) => {
    // Internal matrix component order is W, X, Y, Z. AmbiX stores W, Y, Z, X;
    // FuMa stores W, X, Y, Z with W 3 dB lower, so raise W by sqrt(2).
    const channelForComponent = order === "ambix" ? [0, 3, 1, 2] : [0, 1, 2, 3]
    const omniWeight = 1 / SPEAKERS.length
    const directionalWeight = 3 / SPEAKERS.length

    SPEAKERS.forEach((direction, speakerIndex) => {
      const components = speakerComponents(direction).ambisonic
      const componentWeights = [
        (order === "fuma" ? Math.SQRT2 : 1) * omniWeight,
        components[1] * directionalWeight,
        components[2] * directionalWeight,
        components[3] * directionalWeight,
      ]

      for (let component = 0; component < 4; component += 1) {
        matrix[speakerIndex][channelForComponent[component]].gain.value = componentWeights[component]
      }
    })
  }

  setChannelOrder("ambix")
  return { input, output, setChannelOrder }
}
